import api from "../utils/api-axios";

/**
 * Fetch listings, optionally filtered by radius around a point.
 *
 * @param {object} [params]
 * @param {number} [params.lat]    center latitude (-90 to 90)
 * @param {number} [params.lng]    center longitude (-180 to 180)
 * @param {number} [params.radius] radius in meters (100 to 100000)
 * @returns the raw backend response { success, message, listings }
 */
export const fetchAllListings = async (params = {}) => {
  try {
    const config = {};
    const { lat, lng, radius } = params;

    if (
      typeof lat === "number" &&
      typeof lng === "number" &&
      !Number.isNaN(lat) &&
      !Number.isNaN(lng)
    ) {
      config.params = {
        lat,
        lng,
        ...(radius ? { radius } : {}),
      };
    }

    return await api.get("/listing/", config);
  } catch (error) {
    console.error("Error fetching listings:", error);
    throw error;
  }
};

export const getListingById = async (id) => {
  try {
    const response = await api.get(`/listing/${id}`);
    if (response.success) {
      return response.listings;
    }
    return null;
  } catch (error) {
    console.error("Error fetching listing by ID:", error);
    return null;
  }
};

export const createListing = async (formData) => {
  try {
    return await api.post("/listing/create", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (error) {
    console.error("Error creating listing:", error);
    return {
      success: false,
      message: error?.data?.message || "Something went wrong",
    };
  }
};

export const getListingsByUser = async (id) => {
  try {
    const response = await api.get(`/listing/user/${id}`);
    if (response && response.success) {
      return response;
    }
    return { success: false, error: "Failed to fetch user listings" };
  } catch (error) {
    console.error("Error fetching user listings:", error);
    return { success: false, error: error.message };
  }
};

export const getFeaturedListings = async () => {
  try {
    const response = await api.get("/listing/featured");
    if (response && response.success) {
      return response;
    }
    return { success: false, error: "Failed to fetch featured listings" };
  } catch (error) {
    console.error("Error fetching featured listings:", error);
    return { success: false, error: error.message };
  }
};

export const updateListing = async (listingId, formData) => {
  try {
    const response = await api.put(
      `/listing/update/${listingId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    if (response && response.success) {
      return response;
    }
    return {
      success: false,
      message: response?.message || "Failed to update listing",
    };
  } catch (error) {
    console.error("Error updating listing:", error);
    return {
      success: false,
      message: error?.data?.message || error.message || "Failed to update listing",
    };
  }
};

export const updateListingImage = async (id, imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);

  return await api.put(`/listing/update-image/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteListing = async (listingId) => {
  try {
    return await api.delete(`/listing/delete/${listingId}`);
  } catch (error) {
    console.error("Error deleting listing:", error);
    return {
      success: false,
      message: error?.data?.message || "Server error while deleting listing.",
    };
  }
};

export const saveListing = async (listingId) => {
  try {
    return await api.post("/user/save-listing", { listingId });
  } catch (error) {
    console.error("Error saving listing:", error);
    return {
      success: false,
      message: error?.data?.message || "Failed to save listing",
    };
  }
};

export const unsaveListing = async (listingId) => {
  try {
    return await api.post("/user/unsave-listing", { listingId });
  } catch (error) {
    console.error("Error unsaving listing:", error);
    return {
      success: false,
      message: error?.data?.message || "Failed to unsave listing",
    };
  }
};